import { Link } from 'react-router-dom';
import type { EventItem } from '@/types';
import { statusClass, statusText, trendClass, trendIcon, trendLabel } from '@/utils/format';

interface EventCardProps {
  event: EventItem;
  categoryName?: string;
}

export default function EventCard({ event, categoryName }: EventCardProps) {
  const scoreText =
    event.status === 'upcoming'
      ? 'VS'
      : `${event.homeScore ?? 0} : ${event.awayScore ?? 0}`;

  return (
    <Link
      to={`/detail/${event.id}`}
      data-product-shop="event-card"
      className="group block bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition-colors"
    >
      <div className="flex flex-col lg:flex-row">
        <div className="relative w-full lg:w-52 h-40 lg:h-auto shrink-0 overflow-hidden bg-background-200">
          <img
            src={event.cover}
            alt={event.title}
            title={`${event.title} 赛事详情`}
            className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500"
          />
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-background-50/90 text-foreground-800">
            <i className={`${event.status === 'live' ? 'ri-record-circle-line' : 'ri-time-line'} w-3.5 h-3.5 flex items-center justify-center`} />
            {statusText(event.status)}
          </span>
        </div>

        <div className="flex-1 min-w-0 p-4 md:p-5 flex flex-col">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] ${statusClass(event.status)}`}>
              {statusText(event.status)}
            </span>
            <span className="text-xs text-foreground-500">{event.league}</span>
            {categoryName ? (
              <span className="text-xs text-primary-600">· {categoryName}</span>
            ) : null}
          </div>

          <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950 leading-snug group-hover:text-primary-700 transition-colors line-clamp-2">
            {event.title}
          </h3>

          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <span className="flex items-center gap-1.5 text-sm text-foreground-800">
              <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-[11px]">
                {event.homeTeam.slice(0, 1)}
              </span>
              {event.homeTeam}
            </span>
            <span className="font-heading text-sm font-semibold text-accent-600 px-2">{scoreText}</span>
            <span className="flex items-center gap-1.5 text-sm text-foreground-800">
              <span className="w-6 h-6 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center text-[11px]">
                {event.awayTeam.slice(0, 1)}
              </span>
              {event.awayTeam}
            </span>
          </div>

          <div className="mt-3 flex items-center gap-4 flex-wrap text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line w-3.5 h-3.5 flex items-center justify-center" />
              {event.startTime}
            </span>
            <span className="hidden sm:flex items-center gap-1">
              <i className="ri-map-pin-line w-3.5 h-3.5 flex items-center justify-center" />
              {event.location}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line w-3.5 h-3.5 flex items-center justify-center" />
              热度 {event.heat}
            </span>
          </div>

          <p className="mt-3 text-sm text-foreground-600 leading-relaxed line-clamp-2">
            {event.summary}
          </p>

          <div className="mt-4 lg:hidden flex items-center justify-between gap-3 pt-3 border-t border-background-200">
            <span className="text-xs text-foreground-500">黑5综合指数</span>
            <span className="flex items-center gap-2">
              <span className="font-heading text-lg font-semibold text-foreground-950">
                {event.black5Index.toFixed(1)}
              </span>
              <span className={`flex items-center gap-0.5 text-xs ${trendClass(event.black5Trend)}`}>
                <i className={`${trendIcon(event.black5Trend)} w-3.5 h-3.5 flex items-center justify-center`} />
                {trendLabel(event.black5Trend)}
              </span>
            </span>
          </div>
        </div>

        <div className="hidden lg:flex flex-col justify-center items-end w-40 shrink-0 p-5 border-l border-background-200 bg-background-100/60">
          <span className="text-xs text-foreground-500 mb-1">黑5综合指数</span>
          <span className="font-heading text-3xl font-semibold text-primary-800">
            {event.black5Index.toFixed(1)}
          </span>
          <span className={`mt-1 flex items-center gap-1 text-xs ${trendClass(event.black5Trend)}`}>
            <i className={`${trendIcon(event.black5Trend)} w-3.5 h-3.5 flex items-center justify-center`} />
            {trendLabel(event.black5Trend)}
          </span>
          <span className="mt-3 text-[11px] text-foreground-500">{event.type} · {event.region}</span>
        </div>
      </div>
    </Link>
  );
}